import { ElectronService } from "./../../electron.service";
import { Injectable } from "@angular/core";
import {
  ReadFileData,
  VolumeInfo,
  FileSystemTreeNode,
  FileSystemNodeInfo,
  SelectionMode,
  FileSystemObjectType,
  DriveType,
  FileExtensions,
  FileStats,
  WriteFileRequest,
  FileInfo,
  AccessRule,
  AccessRuleType,
  OperationMode,
  AccessRuleOperation,
  AccessGrant,
} from "./types";
import { FILE_SYSTEM, IPC_CHANNEL } from "../../_offline/electron.constants";
import { normalizeExtension } from '../../_services/image-upload/inspection-file.validator';
import { getFilePathInfo } from "../../_utils/file-system-utils";

export const SYSTEM_DRIVE = "C";

const {
  GET_BRIAN_FOLDERS,
  LIST_DRIVES,
  LIST_FILES_AND_FOLDERS,
  SCAN_FOLDER_FILES,
  FILES_EXIST,
  READ_FILES,
  WRITE_FILES,
} = IPC_CHANNEL.FILE_MANAGER;

@Injectable()
export class FileManagerService {

  static PRIMARY_DRIVE = "C:";

  monitorFolderPath: string | null;

  constructor(private electronService: ElectronService) {}

  async getMonitorFolderPath() {
    if (!this.monitorFolderPath) {
      const brianFolders = await this.electronService.typedInvoke(GET_BRIAN_FOLDERS);
      this.monitorFolderPath = brianFolders[FILE_SYSTEM.PATH.MONITOR.toUpperCase()]?.replace(/[\\/]+$/, "")?.toLowerCase();
    }
    return this.monitorFolderPath;
  }

  getDriveLetter(path: string): string | null {
    const match = path?.match(/^([a-zA-Z])(?::)?(?:\\.*)?$/);
    return match ? match[1].toUpperCase() : null;
  }

  isSystemDrive(driveLetter: string) {
    return driveLetter?.substring(0, 1) === SYSTEM_DRIVE;
  }

  normalizeExtensions(extensions: FileExtensions = []): Set<string> {
    let normalizedExtensions: string[] = [];

    if (extensions instanceof Set) {
      normalizedExtensions = Array.from(extensions).map(normalizeExtension);
    } else if (Array.isArray(extensions)) {
      normalizedExtensions = extensions.map(normalizeExtension);
    } else {
      normalizedExtensions = Object.keys(extensions).map(normalizeExtension);
    }

    normalizedExtensions = normalizedExtensions.filter(Boolean);

    return new Set<string>(normalizedExtensions);
  }

  evaluateAccessRule(
    accessRule: AccessRule,
    volume?: VolumeInfo,
    fileOrFolderPath?: string
  ): {
    access: AccessGrant;
    defaultContentAccess?: Exclude<AccessGrant, AccessGrant.NotDetermined>
  } {
    // Handle composite rules
    if ("operation" in accessRule) {
      switch (accessRule.operation) {
        case AccessRuleOperation.And: {
          const results = accessRule.rules.map(rule => this.evaluateAccessRule(rule, volume, fileOrFolderPath));

          if (results.every(rule => rule.access === AccessGrant.Allow)) {
            return { access: AccessGrant.Allow };
          }

          if (results.some(rule => rule.access === AccessGrant.Deny)) {
            return { access: AccessGrant.Deny };
          }

          return { access: AccessGrant.NotDetermined };
        }
        case AccessRuleOperation.Or: {
          const results = accessRule.rules.map(rule => this.evaluateAccessRule(rule, volume, fileOrFolderPath));

          if (results.some(rule => rule.access === AccessGrant.Allow)) {
            return { access: AccessGrant.Allow };
          }

          if (results.every(rule => rule.access === AccessGrant.Deny)) {
            return { access: AccessGrant.Deny };
          }

          return { access: AccessGrant.NotDetermined };
        }
        case AccessRuleOperation.Not: {
          const isAnyAllow = accessRule.rules.some(rule => this.evaluateAccessRule(rule, volume, fileOrFolderPath).access === AccessGrant.Allow);

          return isAnyAllow ? { access: AccessGrant.Deny } : { access: AccessGrant.Allow };
        }
      }
    }

    // Handle simple rules
    switch (accessRule.type) {
      case AccessRuleType.Drive: {
        if (!volume) {
          return { access: AccessGrant.NotDetermined };
        }

        const isDriveTypeMatch = volume?.driveType === accessRule.driveType;
        const isDriveLetterMatch = volume?.driveLetter?.toLowerCase() === accessRule.driveLetter?.toLowerCase();

        let access: AccessGrant;

        // Check whether both drive type and drive letter restrictions were provided, or only one of them.
        if (accessRule?.driveType && accessRule?.driveLetter) {
          access = isDriveTypeMatch && isDriveLetterMatch ? accessRule.access : AccessGrant.NotDetermined;
        } else {
          access = isDriveTypeMatch || isDriveLetterMatch ? accessRule.access : AccessGrant.NotDetermined;
        }

        const defaultContentAccess: AccessGrant = accessRule.defaultContentAccess;

        return { access, defaultContentAccess };
      }

      case AccessRuleType.Folder: {
        if (!fileOrFolderPath) {
          return { access: AccessGrant.NotDetermined };
        }

        const normalizedPath = fileOrFolderPath.toLowerCase();
        const normalizedRulePath = accessRule.folderPath.toLowerCase();

        const isMatch = accessRule.includeSubfolders
          ? normalizedPath.startsWith(normalizedRulePath)
          : normalizedPath === normalizedRulePath;

        const access = isMatch ? accessRule.access : AccessGrant.NotDetermined;

        return { access };
      }

      case AccessRuleType.File: {
        if (!fileOrFolderPath) {
          return { access: AccessGrant.NotDetermined };
        }
        const isMatch = fileOrFolderPath.toLowerCase() === accessRule.filePath.toLowerCase();

        const access = isMatch ? accessRule.access : AccessGrant.NotDetermined;

        return { access };
      }
    }
  }

  async getOnlineDisksAndVolumes({
    accessRule,
    operationMode
  } : {
    accessRule?: AccessRule,
    operationMode: OperationMode
  }): Promise<FileSystemTreeNode[]> {
    const onlineDisksAndVolumes: VolumeInfo[] = await this.electronService.typedInvoke(LIST_DRIVES);
    const monitorFolderPath = await this.getMonitorFolderPath();

    const nodes: FileSystemTreeNode[] = onlineDisksAndVolumes
      // Verify access
      .filter((diskVolume: VolumeInfo) => {
        const { access: diskAccess } = this.evaluateAccessRule(accessRule, diskVolume, undefined);
        return diskAccess === AccessGrant.Allow;
      })
      // Re-write C: root into Monitor folder path in import mode
      .map((diskVolume: VolumeInfo): VolumeInfo => {
        if (this.isSystemDrive(diskVolume.driveLetter)) {
          return {
            ...diskVolume,
            driveLetter: monitorFolderPath,
            friendlyName: "Local Disk",
          };
        }

        return diskVolume;
      })
      // Map to tree node
      .map((diskVolume: VolumeInfo): FileSystemTreeNode => {
        const { driveType, size, sizeRemaining } = diskVolume;

        const icon = driveType === DriveType.Removable ? "pi pi-folder" : "pi pi-folder-open";

        const isSelectable = operationMode === OperationMode.Export;

        return {
          key: diskVolume.driveLetter,
          label: diskVolume.friendlyName || diskVolume.driveLetter,
          leaf: false,
          selectable: isSelectable,
          partialSelected: false,
          children: null,
          icon,
          // Volume specific fields
          data: {
            type: FileSystemObjectType.Volume,
            driveType: driveType,
            totalSpace: size,
            freeSpace: sizeRemaining,
          },
        };
      });

    return nodes;
  }

  async listFilesAndFolders({
    operationMode,
    selectionMode,
    extensions,
    folderPath,
  }: {
    accessRule?: AccessRule,
    operationMode: OperationMode,
    selectionMode: SelectionMode,
    extensions: Set<string>,
    folderPath: string,
  }): Promise<FileSystemTreeNode[]> {
    const normalizedExtensions = this.normalizeExtensions(extensions);

    const {
      isFileSelectionMode,
      isFolderSelectionMode,
      isFileOrFolderSelectionMode
    } = this.getSelectionModeFlags(selectionMode);

    const driveLetter = this.getDriveLetter(folderPath);

    const monitorFolderPath = await this.getMonitorFolderPath();

    const filesAndFolders: FileSystemNodeInfo[] = await this.electronService.typedInvoke(LIST_FILES_AND_FOLDERS, { folderPath });

    const nodes: FileSystemTreeNode[] = filesAndFolders
      // Verify access
      .filter((fileOrFolder: FileSystemNodeInfo) => {
        if (operationMode === OperationMode.Export) {
          return true;
        }

        // No folder/file access restriction for other visible drives
        const { fullName, isDirectory } = fileOrFolder;

        let allowAccess = true;

        if (this.isSystemDrive(driveLetter)) {
          const lowerCaseFullName = fullName.toLowerCase();

          // Restrict access to Monitor folder for C: drive
          if (isDirectory) {
            allowAccess = (lowerCaseFullName.length < monitorFolderPath.length)
              ? monitorFolderPath.includes(lowerCaseFullName)
              : lowerCaseFullName.startsWith(monitorFolderPath);
          } else {
            // Access all files within Monitor folder
            allowAccess = lowerCaseFullName.startsWith(monitorFolderPath);
          }
        }

        return allowAccess;
      })
      // Filter by extensions
      .filter((fileOrFolder: FileSystemNodeInfo): boolean => {
        let displayEntry = false;

        const { name, isDirectory } = fileOrFolder;

        if (isDirectory) {
          displayEntry = true;
        } else {
          const { fileExtension } = getFilePathInfo(name);
          displayEntry = fileExtension && normalizedExtensions.has(normalizeExtension(fileExtension));
        }

        return displayEntry;
      })
      // Map to tree node
      .map((fileOrFolder: FileSystemNodeInfo): FileSystemTreeNode => {
        const { fullName, name, isDirectory, length } = fileOrFolder;

        const icon = isDirectory ? "pi pi-folder" : "pi pi-file";

        const type = isDirectory ? FileSystemObjectType.Folder : FileSystemObjectType.File;

        const isSelectable = isFileOrFolderSelectionMode || (isDirectory ? isFolderSelectionMode : isFileSelectionMode);

        return {
          icon,
          key: fullName,
          label: name,
          leaf: !isDirectory,
          selectable: isSelectable,
          partialSelected: false,
          children: null,
          // Resource specific fields
          data: {
            type,
            isDirectory,
            size: length,
          },
        };
      });

    return nodes;
  }

  async scanFolderFiles(
    folderPath: string,
    extensions: Set<string>
  ): Promise<string[]> {
    const normalizedExtensions = this.normalizeExtensions(extensions);

    const discoveredFiles: string[] = await this.electronService.typedInvoke(
      SCAN_FOLDER_FILES,
      {
        folderPath,
        extensions: normalizedExtensions
      }
    );

    return discoveredFiles;
  }

  async filesExist(
    fileChecks: FileInfo[]
  ): Promise<FileStats[]> {
    const result: FileStats[] = await this.electronService.typedInvoke(FILES_EXIST, { fileChecks });
    return result;
  }

  async readFiles(
    paths: string[],
    parentDirectory?: string
  ): Promise<File[]> {
    const fileData: ReadFileData[] = await this.electronService.typedInvoke(READ_FILES, { paths });

    const pathSeparator = /[/\\]/;

    return fileData.map((entry: ReadFileData) => {
      const { filePath, mimeType, data } = entry;

      const parentDirectoryName = parentDirectory?.split(pathSeparator).filter(Boolean).pop();

      const webkitRelativePath = parentDirectory
        ? filePath.substring(filePath.indexOf(parentDirectoryName)).replace(/\\/g, '/')
        : entry.fileName;

      const fileName = filePath.split(pathSeparator).pop() || webkitRelativePath;

      const file = new File([new Uint8Array(data)], fileName, { type: mimeType });

      const readOnlyAttributeProps = {
        writable: false,
        enumerable: true,
        configurable: false
      };

      Object.defineProperties(file, {
        webkitRelativePath: {
          value: webkitRelativePath,
          ...readOnlyAttributeProps
        },
        path: {
          value: filePath,
          ...readOnlyAttributeProps
        }
      });

      return file;
    });
  }

  async writeFiles(writeFileRequests: WriteFileRequest[]): Promise<void> {
    await this.electronService.typedInvoke(WRITE_FILES, { writeFileRequests });
  }

  getSelectionModeFlags(selectionMode: SelectionMode) {
    const isFileSelectionMode = [SelectionMode.SingleFile, SelectionMode.MultipleFiles].includes(selectionMode);
    const isFolderSelectionMode = [SelectionMode.SingleFolder, SelectionMode.MultipleFolders].includes(selectionMode);
    const isFileOrFolderSelectionMode = [SelectionMode.SingleFileOrFolder, SelectionMode.MultipleFileOrFolders].includes(selectionMode);

    const isSingleSelectionMode = [SelectionMode.SingleFile, SelectionMode.SingleFolder, SelectionMode.SingleFileOrFolder].includes(selectionMode);
    const isMultipleSelectionMode = [SelectionMode.MultipleFiles, SelectionMode.MultipleFolders, SelectionMode.MultipleFileOrFolders].includes(selectionMode);

    return {
      // Current selection mode
      selectionMode,
      // File or folder selection flags
      isFileSelectionMode,
      isFolderSelectionMode,
      isFileOrFolderSelectionMode,
      // Single or multiple selection flags
      isSingleSelectionMode,
      isMultipleSelectionMode
    };
  }

}
